import { useState, useRef, useEffect, useCallback } from 'react'

const MAX_HISTORY_SIZE = 50

/**
 * Custom hook for undo/redo functionality
 * @param {Object} currentState - The current state to track
 * @param {Function} setState - Function to update the state
 * @param {Function} showToast - Optional function to show toast notifications
 * @returns {Object} - { handleUndo, handleRedo, canUndo, canRedo }
 */
export default function useHistory(currentState, setState, showToast) {
  const [history, setHistory] = useState([])
  const [historyIndex, setHistoryIndex] = useState(-1)

  // Refs to track state without causing re-renders
  const isUndoRedoAction = useRef(false)
  const lastSavedStateRef = useRef('')
  const historyIndexRef = useRef(-1)

  // Keep ref in sync with historyIndex state
  useEffect(() => {
    historyIndexRef.current = historyIndex
  }, [historyIndex])

  // Track state changes for undo history
  useEffect(() => {
    if (isUndoRedoAction.current) {
      isUndoRedoAction.current = false
      return
    }

    if (!currentState || Object.keys(currentState).length === 0) return

    const serializedState = JSON.stringify(currentState)
    // Don't save if state hasn't actually changed
    if (serializedState === lastSavedStateRef.current) return

    lastSavedStateRef.current = serializedState

    const currentIndex = historyIndexRef.current

    setHistory(prev => {
      // Remove any future states if we're not at the end
      const newHistory = prev.slice(0, currentIndex + 1)
      // Add current state
      newHistory.push(serializedState)
      // Limit history size
      if (newHistory.length > MAX_HISTORY_SIZE) {
        newHistory.shift()
      }
      return newHistory
    })

    setHistoryIndex(prev => Math.min(prev + 1, MAX_HISTORY_SIZE - 1))
  }, [currentState])

  // Undo function
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      isUndoRedoAction.current = true
      const prevState = JSON.parse(history[historyIndex - 1])
      setHistoryIndex(prev => prev - 1)
      setState(prevState)
      lastSavedStateRef.current = history[historyIndex - 1]
      if (showToast) showToast('Undo')
    }
  }, [history, historyIndex, setState, showToast])

  // Redo function
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      isUndoRedoAction.current = true
      const nextState = JSON.parse(history[historyIndex + 1])
      setHistoryIndex(prev => prev + 1)
      setState(nextState)
      lastSavedStateRef.current = history[historyIndex + 1]
      if (showToast) showToast('Redo')
    }
  }, [history, historyIndex, setState, showToast])

  return {
    handleUndo,
    handleRedo,
    canUndo: historyIndex > 0,
    canRedo: historyIndex < history.length - 1,
  }
}
